import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ContentManagerCopyMoveModeEnum } from "../../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { RootState } from "../../../src/taskpane/redux";
import { setContentManagerMoveTo } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { Copy20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/Copy20Regular";
import { DocumentCopy20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/DocumentCopy20Regular";
import { getFolderPermissionMenuState, runFolderPermissionMenuAction } from "./folderPermissionMenuState";

export function useMenuItem_CopyTo(
    itemCount: number | undefined,
    selectedItems: any[],
    setShowConfirmMoveToDialog: (isShowConfirmMoveToDialog: boolean) => void,
    isInOneDrive: boolean,
    isFetchingData: boolean,
    setIsCMUploadFilesPanelOpen: (isCMUploadFilesPanelOpen: boolean) => void
) {
    const languageStrings = React.useContext(LanguageContext);
    const dispatch = useDispatch();

    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const workingDirectories = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const moveTo = useSelector((state: RootState) => state.contentManagerDirectory.moveTo);
    const { moveToEnabled, mode: moveToMode, busyMovingFiles, busyFetchingFiles } = moveTo;
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.at(-1));
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const tenantCopyMoveToEnabled = useSelector((state: RootState) => state.tenantInfo?.enableCopyMoveTo);
    const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue);
    const filterSections = useSelector((state: RootState) => state.contentManagerDirectory?.filterSections);

    const handleCopyAll = React.useCallback(() => {
        const sourceSiteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
        dispatch(setContentManagerMoveTo({
            mode: ContentManagerCopyMoveModeEnum.CopyAll,
            moveToEnabled: false,
            workingDirectories: workingDirectories,
            selectedFiles: [],
            sourceSiteUrl,
            busyMovingFiles: false,
            copyJobResults: [],
            isMoveComplete: false,
            isMoveCancelled: false,
            isMoveInProgress: false,
            destinationDirectory: null,
            progressCountCompleted: 0,
            progressCountTotal: 0,
            error: ""
        }));
        setShowConfirmMoveToDialog(true);
    }, [dispatch, isInOneDrive, oneDriveRootDetails, currentDirectory, workingDirectories, setShowConfirmMoveToDialog]);

    const handleCopyToSelected = React.useCallback(() => {
        const sourceSiteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
        dispatch(setContentManagerMoveTo({
            mode: ContentManagerCopyMoveModeEnum.Copy,
            moveToEnabled: false,
            workingDirectories: workingDirectories,
            selectedFiles: selectedItems,
            sourceSiteUrl,
            busyMovingFiles: false,
            copyJobResults: [],
            isMoveComplete: false,
            isMoveCancelled: false,
            isMoveInProgress: false,
            destinationDirectory: null,
            progressCountCompleted: 0,
            progressCountTotal: 0,
            error: ""
        }));
        setShowConfirmMoveToDialog(true);
    }, [dispatch, isInOneDrive, oneDriveRootDetails, currentDirectory, workingDirectories, selectedItems, setShowConfirmMoveToDialog]);

    const handleConfirmCopyDestination = React.useCallback(() => {
        if (moveToMode === ContentManagerCopyMoveModeEnum.CopySelectedEmails || moveToMode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails) {
            setIsCMUploadFilesPanelOpen(true);
        } else {
            setShowConfirmMoveToDialog(true);
        }
    }, [moveToMode, setIsCMUploadFilesPanelOpen, setShowConfirmMoveToDialog]);

    const menuItem_CopyTo = React.useMemo(() => {
        const editPermissionMenuState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canEditListItem"]
        });
        const openPermissionMenuState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canOpenItems"]
        });

        const isConfirmingCopyDestination = moveToEnabled
            && currentDirectory?.type !== ComponentTypes.SharePointList
            && (moveToMode === ContentManagerCopyMoveModeEnum.Copy
                || moveToMode === ContentManagerCopyMoveModeEnum.CopyAll
                || moveToMode === ContentManagerCopyMoveModeEnum.CopySelectedEmails
                || moveToMode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails);

        if (isConfirmingCopyDestination) {
            if (!editPermissionMenuState.shouldRender) {
                return null;
            }

            return {
                key: "confirmCopyTo",
                text: `${languageStrings.Confirm} ${languageStrings.SaveTo}`,
                disabled: editPermissionMenuState.isDisabled || isFetchingData,
                // iconProps: { iconName: "Copy" },
                iconProps: {},
                onRenderIcon: () => <DocumentCopy20Regular className={menuItemIconStyles} />,
                onClick: () => {
                    runFolderPermissionMenuAction(editPermissionMenuState, handleConfirmCopyDestination);
                },
            } as IContextualMenuItem;
        }

        if (!tenantCopyMoveToEnabled) {
            return null;
        }

        const canCopyAll = itemCount > 0
            && selectedItems.length === 0
            && !moveToEnabled
            && currentDirectory?.type !== ComponentTypes.SharePointList;

        if (canCopyAll) {
            const isFilterActive = filterSections?.some(section =>
                section.items?.some(item => item.selected === true) ||
                section.daysOffset ||
                section.daysOffset === 0
            );

            const isSearchActive = searchValue && (searchValue.value || searchValue.filterType !== "");

            if (isFilterActive || isSearchActive) {
                return null;
            }

            if (!openPermissionMenuState.shouldRender) {
                return null;
            }

            return {
                key: "copyAll",
                text: languageStrings.CopyAll,
                disabled: openPermissionMenuState.isDisabled || isFetchingData || busyMovingFiles || busyFetchingFiles,
                // iconProps: { iconName: "Copy" },
                iconProps: {},
                onRenderIcon: () => <Copy20Regular className={menuItemIconStyles} />,
                onClick: () => {
                    runFolderPermissionMenuAction(openPermissionMenuState, handleCopyAll);
                },
            } as IContextualMenuItem;
        }

        const canCopyToSelected = itemCount > 0 && selectedItems.length > 0 && !moveToEnabled && currentDirectory?.type !== ComponentTypes.SharePointList && !selectedItems.some(x => x?.EffectiveBasePermissions?.canOpenItems === false);
        if (canCopyToSelected) {
            return {
                key: "copyTo",
                text: languageStrings.CopyTo,
                // iconProps: { iconName: "Copy" },
                iconProps: {},
                onRenderIcon: () => <DocumentCopy20Regular className={menuItemIconStyles} />,
                onClick: handleCopyToSelected,
            } as IContextualMenuItem;
        }

        return null;

    }, [
        tenantCopyMoveToEnabled,
        itemCount,
        selectedItems,
        moveToEnabled,
        currentDirectory,
        moveToMode,
        isFetchingData,
        busyMovingFiles,
        busyFetchingFiles,
        currentSharepointFolder?.userPermissions,
        currentSharepointFolder?.permissionStatus,
        searchValue,
        filterSections,
        languageStrings,
        handleCopyAll,
        handleCopyToSelected,
        handleConfirmCopyDestination,
        currentSharepointFolder
    ]);

    return {
        menuItem_CopyTo
    };
}
